import { useState } from 'react';
import type { FieldData, QuoteInputs, QuoteCalculation } from '../types';
import { calculateQuote } from '../utils/quoteCalculator';

interface QuoteFormProps {
  fieldData: FieldData;
  onQuoteCalculated: (calculation: QuoteCalculation) => void;
}

export default function QuoteForm({ fieldData, onQuoteCalculated }: QuoteFormProps) {
  const [inputs, setInputs] = useState<QuoteInputs>({
    applicationType: 'fungicide',
    applicationMonth: new Date().getMonth() + 1,
    numberOfApplications: 2,
    twoAppCommitment: false,
    treeOverhang: 'light',
    hillsSlope: 'flat',
    multipleFields: false,
  });

  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (inputs.multipleFields && !inputs.fieldSpacing) {
      setError('Please specify the distance between fields');
      return;
    }

    if (inputs.numberOfApplications === 2) {
      setInputs(prev => ({ ...prev, twoAppCommitment: true }));
    }

    try {
      const calculation = calculateQuote(fieldData, inputs);
      onQuoteCalculated(calculation);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to calculate quote');
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Application Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Application Type
          </label>
          <select
            value={inputs.applicationType}
            onChange={(e) => setInputs({ ...inputs, applicationType: e.target.value as any })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
          >
            <option value="herbicide">Herbicide</option>
            <option value="fungicide">Fungicide</option>
            <option value="insecticide">Insecticide</option>
            <option value="foliar-nutrition">Foliar Nutrition</option>
            <option value="pgr">Plant Growth Regulators</option>
          </select>
        </div>

        {/* Application Month */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Planned Application Month
          </label>
          <select
            value={inputs.applicationMonth}
            onChange={(e) => setInputs({ ...inputs, applicationMonth: parseInt(e.target.value) })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
          >
            {[
              'January', 'February', 'March', 'April', 'May', 'June',
              'July', 'August', 'September', 'October', 'November', 'December'
            ].map((month, idx) => (
              <option key={month} value={idx + 1}>{month}</option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Peak season months (May-August) may have +10% demand pricing
          </p>
        </div>

        {/* Number of Applications */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Number of Applications This Season
          </label>
          <div className="grid grid-cols-3 gap-2">
            {[1, 2, 3, 4, 5, 6].map((num) => (
              <button
                key={num}
                type="button"
                onClick={() => setInputs({ ...inputs, numberOfApplications: num as any })}
                className={`px-4 py-3 rounded-lg font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  inputs.numberOfApplications === num
                    ? 'bg-emerald-800 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {num}
              </button>
            ))}
          </div>
          {inputs.numberOfApplications === 2 && (
            <div className="mt-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-900">
              <i className="ri-discount-percent-line mr-1"></i>
              2-application commitment: 10% discount applied
            </div>
          )}
        </div>

        {/* Tree Overhang */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Tree Overhang / Edge Habitat
          </label>
          <select
            value={inputs.treeOverhang}
            onChange={(e) => setInputs({ ...inputs, treeOverhang: e.target.value as any })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
          >
            <option value="none">None - Open field</option>
            <option value="light">Light - Few trees on edges</option>
            <option value="moderate">Moderate - Tree lines on 1-2 sides</option>
            <option value="heavy">Heavy - Surrounded by trees</option>
          </select>
        </div>

        {/* Hills/Slope */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Terrain / Slope
          </label>
          <select
            value={inputs.hillsSlope}
            onChange={(e) => setInputs({ ...inputs, hillsSlope: e.target.value as any })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
          >
            <option value="flat">Flat - No significant slope</option>
            <option value="gentle">Gentle - Slight rolling hills</option>
            <option value="moderate">Moderate - Noticeable slopes</option>
            <option value="steep">Steep - Significant elevation changes</option>
          </select>
        </div>

        {/* Multiple Fields */}
        <div>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={inputs.multipleFields}
              onChange={(e) => setInputs({ ...inputs, multipleFields: e.target.checked })}
              className="w-5 h-5 text-emerald-800 border-gray-300 rounded focus:ring-emerald-500 cursor-pointer"
            />
            <span className="text-sm font-medium text-gray-700">
              Multiple fields at different locations
            </span>
          </label>
        </div>

        {inputs.multipleFields && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Average Distance Between Fields (miles)
            </label>
            <input
              type="number"
              value={inputs.fieldSpacing || ''}
              onChange={(e) => setInputs({ ...inputs, fieldSpacing: parseFloat(e.target.value) || undefined })}
              step="0.1"
              min="0.1"
              placeholder="e.g., 2.5"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
              required
            />
          </div>
        )}

        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {error}
          </div>
        )}

        <button
          type="submit"
          className="w-full bg-emerald-800 text-white font-bold py-4 rounded-lg hover:bg-emerald-700 active:bg-emerald-900 transition-colors cursor-pointer whitespace-nowrap"
        >
          <span className="flex items-center justify-center gap-2">
            <i className="ri-calculator-line"></i>
            Calculate My Quote
          </span>
        </button>
      </form>

      {/* Info Box */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start gap-3">
          <i className="ri-information-line text-blue-600 text-lg mt-0.5"></i>
          <div className="text-sm text-blue-900">
            <p className="font-medium mb-1">Pricing Transparency</p>
            <p className="text-blue-800">
              Your quote is calculated based on field size, shape complexity, terrain, and seasonal demand. All factors are shown in your results.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
